/**
 * Server-side flow graph templates for marketplace industry packs.
 *
 * Before this file existed, "installing" a pack recorded a MarketplaceInstall
 * row and created NOTHING — the static catalog items in MarketplacePage.tsx are
 * display-only (no graphs). These are the real React-Flow graphs (Flow.nodes /
 * Flow.edges) the executor runs, authored against the node vocabulary in
 * apps/api/src/services/flows/flow-executor.ts:
 *
 *   node types:   trigger | message | condition | action | ai_response |
 *                 escalate | wait | set_variable | webhook | collect_input
 *   action types: add_tag | assign_agent | create_ticket | trigger_webhook |
 *                 create_lead | send_email
 *
 * Installed flows are ALWAYS created with is_active: false (drafts) — the org
 * reviews and customizes before anything fires. Titles must stay in sync with
 * MARKETPLACE_FLOW_MAP (industries.ts) so FlowsPage's "Already active via
 * Marketplace" detection keeps working.
 */

import { MARKETPLACE_FLOW_MAP } from './industries';

export interface FlowTemplateNode {
  id: string;
  type: string;
  data: Record<string, unknown>;
  /** React-Flow canvas position so installed drafts render as a readable column */
  position: { x: number; y: number };
}

export interface FlowTemplateEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: 'yes' | 'no';
}

export interface FlowTemplate {
  title: string;
  description: string;
  trigger_type:
    | 'conversation_started'
    | 'message_received'
    | 'ticket_created'
    | 'url_match'
    | 'time_on_page'
    | 'first_visit'
    | 'segment_match'
    | 'nps_trigger'
    | 'out_of_hours';
  trigger_config: Record<string, unknown>;
  nodes: FlowTemplateNode[];
  edges: FlowTemplateEdge[];
}

// ─── Graph helpers (keep the literals below readable) ────────────────────────

function col(idx: number): { x: number; y: number } {
  return { x: 80, y: idx * 140 };
}

function chain(nodes: FlowTemplateNode[]): FlowTemplateEdge[] {
  const edges: FlowTemplateEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({ id: `e${i}`, source: nodes[i].id, target: nodes[i + 1].id });
  }
  return edges;
}

function linear(
  title: string,
  description: string,
  trigger_type: FlowTemplate['trigger_type'],
  trigger_config: Record<string, unknown>,
  bodies: Array<Pick<FlowTemplateNode, 'id' | 'type' | 'data'>>,
): FlowTemplate {
  const nodes: FlowTemplateNode[] = [
    { id: 'trigger', type: 'trigger', data: { trigger_type }, position: col(0) },
    ...bodies.map((b, i) => ({ ...b, position: col(i + 1) })),
  ];
  return { title, description, trigger_type, trigger_config, nodes, edges: chain(nodes) };
}

// ─── The 12 archetype graphs ──────────────────────────────────────────────────

export const FLOW_ARCHETYPES: Record<string, FlowTemplate> = {
  'Out-of-Hours Auto-Reply': linear(
    'Out-of-Hours Auto-Reply',
    'Sets expectations when a visitor messages outside business hours, and tags the conversation for morning follow-up.',
    'out_of_hours',
    {},
    [
      {
        id: 'm1',
        type: 'message',
        data: {
          content:
            "Thanks for reaching out! Our team is away right now, but I'm happy to help with most questions. If you need a person, leave your message and contact details — we'll follow up first thing.",
        },
      },
      { id: 'a1', type: 'action', data: { action_type: 'add_tag', tag: 'after-hours' } },
    ],
  ),

  'Collect Lead Info': linear(
    'Collect Lead Info',
    'Captures the visitor’s name and email conversationally and records a lead you can follow up on.',
    'conversation_started',
    {},
    [
      {
        id: 'm1',
        type: 'message',
        data: { content: 'Welcome! Before we dive in, may I grab a couple of quick details so the right person can follow up if needed?' },
      },
      {
        id: 'ci_name',
        type: 'collect_input',
        data: { field_key: 'name', input_type: 'text', prompt: 'What’s your name?' },
      },
      {
        id: 'ci_email',
        type: 'collect_input',
        data: { field_key: 'email', input_type: 'email', prompt: 'And the best email to reach you?' },
      },
      { id: 'a_lead', type: 'action', data: { action_type: 'create_lead' } },
      {
        id: 'm2',
        type: 'message',
        data: { content: 'Perfect, thank you! Now — how can I help today?' },
      },
    ],
  ),

  'Quote Request Form': linear(
    'Quote Request Form',
    'Triggers when a visitor mentions quotes or pricing; gathers contact details plus what they need quoted, then records a qualified lead.',
    'message_received',
    { keywords: ['quote', 'pricing', 'estimate', 'rate', 'how much'] },
    [
      {
        id: 'm1',
        type: 'message',
        data: { content: 'Happy to help with a quote! I’ll grab a few details and the right person will get back to you quickly.' },
      },
      {
        id: 'ci_name',
        type: 'collect_input',
        data: { field_key: 'name', input_type: 'text', prompt: 'What’s your name?' },
      },
      {
        id: 'ci_email',
        type: 'collect_input',
        data: { field_key: 'email', input_type: 'email', prompt: 'What’s the best email for your quote?' },
      },
      {
        id: 'ci_details',
        type: 'collect_input',
        data: {
          field_key: 'quote_details',
          input_type: 'text',
          prompt: 'Briefly, what would you like a quote for?',
        },
      },
      { id: 'a_lead', type: 'action', data: { action_type: 'create_lead' } },
      {
        id: 'm2',
        type: 'message',
        data: { content: 'Got it — thank you! A team member will follow up with your quote shortly. Anything else I can help with meanwhile?' },
      },
    ],
  ),

  'Demo / Meeting Booking': linear(
    'Demo / Meeting Booking',
    'Captures contact details when a visitor asks to book a meeting, demo, or consultation, and records a lead for scheduling follow-up.',
    'message_received',
    { keywords: ['demo', 'appointment', 'book', 'schedule', 'meeting', 'consult'] },
    [
      {
        id: 'm1',
        type: 'message',
        data: { content: 'I’d love to get that set up for you! Let me grab a couple of details.' },
      },
      {
        id: 'ci_name',
        type: 'collect_input',
        data: { field_key: 'name', input_type: 'text', prompt: 'What’s your name?' },
      },
      {
        id: 'ci_email',
        type: 'collect_input',
        data: { field_key: 'email', input_type: 'email', prompt: 'What email should the invitation go to?' },
      },
      { id: 'a_lead', type: 'action', data: { action_type: 'create_lead' } },
      {
        id: 'm2',
        type: 'message',
        data: { content: 'All set — we’ll send a booking link to your email shortly!' },
      },
    ],
  ),

  'Order Status Lookup': linear(
    'Order Status Lookup',
    'Recognizes order/shipping questions and has the AI walk the customer through tracking, escalating if they’re frustrated.',
    'message_received',
    { keywords: ['order', 'tracking', 'shipped', 'delivery', 'package', 'where is my'] },
    [
      {
        id: 'ai1',
        type: 'ai_response',
        data: {
          custom_prompt:
            'The customer is asking about an order. Ask for their order number and the email used at checkout, then explain how to track the order using the knowledge base. If they seem frustrated or the order is significantly delayed, apologize and offer to connect them with a team member.',
        },
      },
    ],
  ),

  'Abandoned Checkout Recovery': linear(
    'Abandoned Checkout Recovery',
    'Waits a few minutes after a visitor lingers on checkout, then gently offers help completing the purchase.',
    'url_match',
    { url_pattern: 'checkout' },
    [
      { id: 'w1', type: 'wait', data: { duration: 10, unit: 'minutes' } },
      {
        id: 'm1',
        type: 'message',
        data: { content: 'Still thinking it over? If anything’s unclear — shipping, sizing, payment options — I’m right here to help you finish up.' },
      },
      { id: 'a1', type: 'action', data: { action_type: 'add_tag', tag: 'abandoned-checkout' } },
    ],
  ),

  'Escalate Urgent Issues': linear(
    'Escalate Urgent Issues',
    'Flags messages containing urgency keywords for immediate human attention and reassures the customer.',
    'message_received',
    { keywords: ['urgent', 'emergency', 'asap', 'immediately', 'right away', 'complaint'] },
    [
      { id: 'esc1', type: 'escalate', data: { reason: 'Customer flagged urgency' } },
      {
        id: 'm1',
        type: 'message',
        data: { content: 'I’ve flagged this as urgent for our team — a person will be with you as soon as possible.' },
      },
    ],
  ),

  'CSAT Survey': linear(
    'CSAT Survey',
    'Asks for a quick satisfaction rating at survey time so you can track service quality.',
    'nps_trigger',
    {},
    [
      {
        id: 'ci_rating',
        type: 'collect_input',
        data: {
          field_key: 'csat_rating',
          input_type: 'select',
          prompt: 'Before you go — how did we do today?',
          options: ['Great', 'Okay', 'Not good'],
        },
      },
      {
        id: 'm1',
        type: 'message',
        data: { content: 'Thanks for the feedback — it genuinely helps us improve!' },
      },
    ],
  ),

  'Route by Department': linear(
    'Route by Department',
    'Asks what the visitor needs and tags the conversation by department so your team can triage instantly. Customize the options and add assignment rules for your team.',
    'conversation_started',
    {},
    [
      {
        id: 'ci_dept',
        type: 'collect_input',
        data: {
          field_key: 'department',
          input_type: 'select',
          prompt: 'What can we help you with today?',
          options: ['Sales', 'Support', 'Billing'],
        },
      },
      { id: 'a1', type: 'action', data: { action_type: 'add_tag', tag: 'routed' } },
    ],
  ),

  'Churn Risk Detection': linear(
    'Churn Risk Detection',
    'Watches for cancellation language, tags the conversation as churn risk, and alerts your team.',
    'message_received',
    { keywords: ['cancel', 'refund', 'switching', 'unsubscribe', 'disappointed', 'not happy'] },
    [
      { id: 'a1', type: 'action', data: { action_type: 'add_tag', tag: 'churn-risk' } },
      { id: 'esc1', type: 'escalate', data: { reason: 'Possible churn risk — review this conversation' } },
    ],
  ),

  'FAQ Self-Service Deflection': linear(
    'FAQ Self-Service Deflection',
    'Lets the AI answer common questions straight from your knowledge base, deflecting tickets before they’re created.',
    'message_received',
    { keywords: ['how do i', 'how can i', 'question', 'help with', 'where can i'] },
    [
      {
        id: 'ai1',
        type: 'ai_response',
        data: {
          custom_prompt:
            'Answer the customer’s question using the knowledge base. Be concise and link or reference the relevant help article when one exists. If you cannot answer confidently from the knowledge base, say so and offer to connect them with a team member.',
        },
      },
    ],
  ),

  'Free Trial Activation': linear(
    'Free Trial Activation',
    'Welcomes new trial signups, captures their email, and records a lead so sales can nurture the trial.',
    'conversation_started',
    { url_pattern: 'trial' },
    [
      {
        id: 'm1',
        type: 'message',
        data: { content: 'Welcome aboard! 🎉 I can help you get set up. What are you hoping to accomplish first?' },
      },
      {
        id: 'ci_email',
        type: 'collect_input',
        data: { field_key: 'email', input_type: 'email', prompt: 'What’s the email on your account, so we can send setup tips?' },
      },
      { id: 'a_lead', type: 'action', data: { action_type: 'create_lead' } },
    ],
  ),
};

// ─── Pack → flow items (mirrors FEATURED_PACKS itemIds in MarketplacePage) ───

export const PACK_FLOW_ITEM_IDS: Record<string, string[]> = {
  'pack-insurance': ['sg-flow-insurance-quote', 'sg-flow-out-of-hours', 'sg-flow-lead-capture'],
  'pack-ecommerce': ['sg-flow-order-status', 'sg-flow-abandoned-cart'],
  'pack-healthcare': ['sg-flow-healthcare-appt', 'sg-flow-out-of-hours'],
  'pack-realestate': ['sg-flow-realestate-listing', 'sg-flow-lead-capture', 'sg-flow-out-of-hours'],
  'pack-legal': ['sg-flow-legal-intake', 'sg-flow-route-dept'],
  'pack-financial': ['sg-flow-financial-consult', 'sg-flow-lead-capture', 'sg-flow-route-dept'],
  'pack-home-services': ['sg-flow-home-estimate', 'sg-flow-out-of-hours', 'sg-flow-csat'],
  'pack-beauty': ['sg-flow-beauty-booking', 'sg-flow-csat', 'sg-flow-out-of-hours'],
  'pack-fitness': ['sg-flow-fitness-trial', 'sg-flow-lead-capture', 'sg-flow-csat'],
  'pack-mental-health': ['sg-flow-therapy-intake', 'sg-flow-out-of-hours'],
  'pack-saas': ['sg-flow-saas-trial', 'sg-flow-churn', 'sg-flow-faq'],
  'pack-hospitality': ['sg-flow-hospitality-reservation', 'sg-flow-csat', 'sg-flow-out-of-hours'],
};

/** IndustryKey → FEATURED_PACKS/AGENT_PACKS id (the 12 verticals with packs). */
export const INDUSTRY_PACK_MAP: Record<string, string> = {
  'Insurance': 'pack-insurance',
  'E-Commerce': 'pack-ecommerce',
  'Healthcare': 'pack-healthcare',
  'Real Estate': 'pack-realestate',
  'Legal': 'pack-legal',
  'Financial Services': 'pack-financial',
  'Home Services': 'pack-home-services',
  'Beauty & Salon': 'pack-beauty',
  'Fitness & Wellness': 'pack-fitness',
  'Mental Health / Therapy': 'pack-mental-health',
  'SaaS / Software': 'pack-saas',
  'Hospitality': 'pack-hospitality',
};

export function packForIndustry(industry: string | null | undefined): string | null {
  if (!industry) return null;
  return INDUSTRY_PACK_MAP[industry] ?? null;
}

export interface PackFlowResolution {
  item_id: string;
  template: FlowTemplate;
}

/**
 * Resolve the flow templates a pack installs. De-duplicates archetypes —
 * e.g. pack-realestate lists both sg-flow-realestate-listing and
 * sg-flow-lead-capture, which are the same 'Collect Lead Info' graph.
 * Pure — unit-tested.
 */
export function flowTemplatesForPack(packId: string): PackFlowResolution[] {
  const itemIds = PACK_FLOW_ITEM_IDS[packId] ?? [];
  const seenTitles = new Set<string>();
  const out: PackFlowResolution[] = [];
  for (const itemId of itemIds) {
    const title = MARKETPLACE_FLOW_MAP[itemId];
    if (!title || seenTitles.has(title)) continue;
    const template = FLOW_ARCHETYPES[title];
    if (!template) continue;
    seenTitles.add(title);
    out.push({ item_id: itemId, template });
  }
  return out;
}
